/* eslint-disable react/require-default-props */
import React from 'react';
import {View} from 'react-native';
import {useSelector} from 'react-redux';
import {get} from 'lodash';

import Text from '/component/base/Text';
import {DashboardID} from '/constant/TestIDs';
import {useTranslation} from '/provider/TranslationProvider';
import {
  variablesBannerText,
  variablesFeatureFlag,
} from '/redux/selector/remoteConfig.selector';
import {getDateUtilDefaultInstance} from '/util/date.util';
import {shouldReleaseFeature} from '/util/featureRelease.util';
import {openURLInNewTabOnWeb} from '/util/linking.util';
import testProperties from '/util/testing/testProperties';

import TouchableOpacity from '../TouchableOpacity';

import {styles} from './BonusProgressBar.style';
import {
  generateOnLearnMoreCSB,
  getDaysLeftForBonus,
  getEarnedCSBAmount,
  getUpdatedEarnedCSBAmount,
  getUpdatedEarnedCSBV5Amount,
  higherIntermidiateCSB,
  intemidiateCSBValue,
  lowerIntermidiateCSB,
  maxCSB,
  maximumCSB,
  maximumCSBV5,
} from './BonusProgressBar.util';
import {OEBox} from './OEBox';

interface BonusProgressBarProps {
  newOrderingEntities?: number;
  currentDate?: string;
  isBanner?: boolean;
  earnedBonusAmount?: string;
  isEarnedBonusCard?: boolean;
  popoverShow?: () => void;
  currentScreen?: string;
  isDasboard?: boolean;
  reactivatedOECount?: number;
  newOECount?: number;
  totalOECount?: number;
}

export const BonusProgressBarCSB: React.FC<BonusProgressBarProps> = ({
  newOrderingEntities = 0,
  currentDate = '',
  isBanner = false,
  earnedBonusAmount = '',
  isEarnedBonusCard = false,
  popoverShow = () => {},
  currentScreen = '',
  isDasboard = false,
  reactivatedOECount = 0,
  newOECount = 0,
  totalOECount = 0,
}) => {
  const {ts, tt} = useTranslation('bonusProgressBarCSB');
  const featureFlagBannerText = useSelector(variablesBannerText);
  const featureFlag = useSelector(variablesFeatureFlag);

  const dateUtil = getDateUtilDefaultInstance();
  const currentDateFormatted = dateUtil.formatDate(currentDate, 'MMMM yyyy');
  const daysLeftForBonus = getDaysLeftForBonus({currentDate});
  const showUpdatedCSBPlus = get(featureFlag, 'showUpdatedCSBPlus', false);
  const showEnhancedCSBPlus = get(featureFlag, 'showEnhancedCSBPlus', false);

  const showCsbPlusInsightsFiltering = shouldReleaseFeature(
    get(featureFlag, 'ff_csb_plus_insights_filtering', ''),
  );
  const currentDateTestId = isDasboard
    ? DashboardID.dashboardCsbBannerCurrentDate
    : DashboardID.dashboardProgressBarCurrentDate;
  const daysLeftTestId = isDasboard
    ? DashboardID.dashboardCsbBannerDaysLeft
    : DashboardID.dashboardProgressBarDaysLeft;

  const earnedBonus = showCsbPlusInsightsFiltering
    ? getUpdatedEarnedCSBV5Amount({newOrderingEntities: newOECount})
    : showEnhancedCSBPlus
    ? getUpdatedEarnedCSBAmount({newOrderingEntities: totalOECount, newOECount})
    : showUpdatedCSBPlus
    ? getUpdatedEarnedCSBAmount({newOrderingEntities})
    : getEarnedCSBAmount({newOrderingEntities});

  const CSBPlusDetails = featureFlagBannerText?.CSBPlusDetails;

  const pqvText = showCsbPlusInsightsFiltering
    ? CSBPlusDetails?.CSBPV5PQVText ||
      'Qualified New Clients with at least 275 PQV.'
    : showEnhancedCSBPlus
    ? CSBPlusDetails?.newPQVText ||
      'Qualified Clients (at least 2 MUST be new) with at least 150 PQV.'
    : CSBPlusDetails?.origPQVText || 'New Clients with at least 150 PQV.';

  const PQVTextURL =
    showEnhancedCSBPlus || showCsbPlusInsightsFiltering
      ? CSBPlusDetails?.PQVTextURL ||
        'https://coachanswers.optavia.com/help/s/article/client-support-bonus-plus-us?language=en_US'
      : '';

  const bonusAmount = isEarnedBonusCard ? earnedBonusAmount : earnedBonus;
  const boxesArray: any = [];
  const maxIndexValue = showCsbPlusInsightsFiltering
    ? 10
    : showUpdatedCSBPlus || showEnhancedCSBPlus
    ? 7
    : 5;

  for (let i = 0; i < maxIndexValue; i++) {
    let threshold;

    if (showCsbPlusInsightsFiltering) {
      threshold = newOECount;
    } else if (newOECount >= 2) {
      threshold = showEnhancedCSBPlus ? totalOECount : newOrderingEntities;
    } else {
      threshold = showEnhancedCSBPlus ? 0 : newOrderingEntities;
    }

    if (i < threshold) {
      boxesArray.push({isChecked: true});
    } else {
      boxesArray.push({isChecked: false});
    }
  }

  const renderBonusAmountPerOE = () => (
    <View style={styles.emptyBoxesContainer} accessible={true}>
      {boxesArray.map((item, index) => {
        if (index === 2) {
          return (
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              emphasis="medium"
              variant="caption"
              style={showEnhancedCSBPlus ? styles.emptyBoxNew : styles.emptyBox}
              key={index}
              {...testProperties(
                DashboardID.bonusProgressBarMinThresholdLabel,
              )}>
              {intemidiateCSBValue}
            </Text>
          );
        }
        if (index === 4) {
          return (
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              emphasis="medium"
              variant="caption"
              style={showEnhancedCSBPlus ? styles.emptyBoxNew : styles.emptyBox}
              key={index}
              {...testProperties(
                DashboardID.bonusProgressBarMaxThresholdLabel,
              )}>
              {maxCSB} FCV
            </Text>
          );
        }
        return (
          <View
            style={showEnhancedCSBPlus ? styles.emptyBoxNew : styles.emptyBox}
            key={index}
          />
        );
      })}
    </View>
  );
  const renderBonusAmountPerOEForUpdatedCSBPlus = () => (
    <View
      accessible={true}
      style={
        showEnhancedCSBPlus
          ? isDasboard
            ? styles.emptyBoxesContainer
            : styles.emptyBoxesContainerReport
          : styles.emptyBoxesContainer
      }>
      {boxesArray.map((_item: any, index: React.Key | null | undefined) => {
        if (index === 1) {
          return (
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              emphasis="medium"
              variant="caption"
              style={
                showEnhancedCSBPlus
                  ? styles.lowerIntermidiateCSBNew
                  : styles.lowerIntermidiateCSB
              }
              key={index}
              {...testProperties(
                DashboardID.bonusProgressBarMinThresholdLabel,
              )}>
              {showEnhancedCSBPlus
                ? `Clients: ${totalOECount}`
                : `${lowerIntermidiateCSB} FCV`}
            </Text>
          );
        }
        if (index === 4) {
          return (
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              emphasis="medium"
              variant="caption"
              style={
                showEnhancedCSBPlus
                  ? styles.centerIntermidiateCSB
                  : styles.higherIntermidiateCSB
              }
              key={index}
              {...testProperties(
                DashboardID.bonusProgressBarMidThresholdLabel,
              )}>
              {showEnhancedCSBPlus
                ? `New: ${newOECount}`
                : `${higherIntermidiateCSB} FCV`}
            </Text>
          );
        }
        if (index === 6) {
          return (
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              emphasis="medium"
              variant="caption"
              style={
                showEnhancedCSBPlus
                  ? styles.higherIntermidiateCSBNew
                  : undefined
              }
              key={index}
              {...testProperties(
                DashboardID.bonusProgressBarMaxThresholdLabel,
              )}>
              {showEnhancedCSBPlus
                ? `Reactivated: ${reactivatedOECount}`
                : `${maximumCSB} FCV`}
            </Text>
          );
        }
        return (
          <View
            style={showEnhancedCSBPlus ? styles.emptyBoxNew : styles.emptyBox}
            key={index}
          />
        );
      })}
    </View>
  );

  const renderBonusAmountPerOEForCSBPV5 = () => (
    <View style={styles.emptyBoxesContainer} accessible={true}>
      {boxesArray?.map((_item: any, index: React.Key | null | undefined) => {
        return (
          <View
            style={
              showCsbPlusInsightsFiltering
                ? styles.emptyBoxNew
                : styles.emptyBox
            }
            key={index}
          />
        );
      })}
    </View>
  );
  const renderDaysLeft = () => {
    if (daysLeftForBonus > 0) {
      return tt('daysLeft', () => (
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          accessible={true}
          accessibilityRole="text"
          accessibilityLabel={`${daysLeftForBonus} days left`}
          style={styles.daysLeft}
          emphasis="medium"
          variant="caption"
          {...testProperties(daysLeftTestId)}>
          {daysLeftForBonus} days left
        </Text>
      ));
    }

    return tt('lastDay', () => (
      <Text
        allowFontScaling
        maxFontSizeMultiplier={2}
        style={styles.daysLeft}
        emphasis="medium"
        variant="caption"
        {...testProperties(daysLeftTestId)}
        accessible={true}
        accessibilityRole="text"
        accessibilityLabel="Last day">
        Last day
      </Text>
    ));
  };

  const renderTitle = () => {
    const checkedCount = boxesArray.filter(box => box.isChecked).length;
    return tt('content', () => (
      <>
        <View
          {...testProperties(DashboardID.dashboardProgressBarLabel)}
          accessible={true}
          accessibilityLabel={`${checkedCount} ${pqvText}`}>
          <Text allowFontScaling maxFontSizeMultiplier={2}>
            {ts('newClientsWith', pqvText)}
          </Text>
        </View>

        {(showEnhancedCSBPlus || showCsbPlusInsightsFiltering) && (
          <TouchableOpacity
            {...testProperties(DashboardID.dashboardCsbBannerLearnMoreLink)}
            accessible={true}
            accessibilityRole="link"
            onPress={() => {
              if (currentScreen === 'Dashboard') {
                const callback = generateOnLearnMoreCSB(
                  {popoverShow},
                  currentScreen,
                  PQVTextURL,
                );
                if (typeof callback === 'function') callback();
              } else {
                openURLInNewTabOnWeb(PQVTextURL);
              }
            }}>
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              style={styles.link}
              isBold
              accessible={true}>
              {ts('learnMore', ' Learn more')}
            </Text>
          </TouchableOpacity>
        )}
      </>
    ));
  };
  return (
    <View>
      <View
        style={styles.heading}
        accessible={true}
        accessibilityLabel={`Current date: ${currentDateFormatted}. ${daysLeftForBonus} days left. Bonus: ${bonusAmount} of ${maxCSB} FCV`}>
        <View style={styles.subHeading} accessible={true}>
          <Text
            allowFontScaling
            maxFontSizeMultiplier={2}
            style={styles.dateText}
            variant={isBanner ? 'heading' : 'small'}
            {...testProperties(currentDateTestId)}
            accessible={true}>
            {currentDateFormatted}
          </Text>
          {!isEarnedBonusCard && renderDaysLeft()}
        </View>
        {tt('moneyEarned', () =>
          isEarnedBonusCard ? (
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              {...testProperties(DashboardID.dashboardProgressBarEarned)}
              style={styles.fcvText}
              accessible={true}>
              <Text
                allowFontScaling
                maxFontSizeMultiplier={2}
                style={styles.moneyBold}>
                {bonusAmount}
              </Text>
            </Text>
          ) : (
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              {...testProperties(DashboardID.dashboardProgressBarEarned)}
              style={styles.fcvText}
              accessible={true}>
              <Text
                allowFontScaling
                maxFontSizeMultiplier={2}
                style={styles.moneyBold}
                accessible={true}>
                {bonusAmount}
              </Text>{' '}
              of{' '}
              {showCsbPlusInsightsFiltering
                ? maximumCSBV5
                : showUpdatedCSBPlus || showEnhancedCSBPlus
                ? maximumCSB
                : maxCSB}{' '}
              FCV
            </Text>
          ),
        )}
      </View>
      {!isEarnedBonusCard && (
        <>
          {renderTitle()}
          <View
            style={
              showEnhancedCSBPlus
                ? styles.boxesContainerNew
                : styles.boxesContainer
            }>
            {boxesArray.map(
              ({isChecked}: any, index: React.Key | null | undefined) => (
                <OEBox
                  isChecked={isChecked}
                  number={Number(index) + 1}
                  key={index}
                />
              ),
            )}
          </View>
          {showEnhancedCSBPlus && !isDasboard && (
            <View style={styles.groupItemWithBorder} />
          )}
          {showCsbPlusInsightsFiltering
            ? renderBonusAmountPerOEForCSBPV5()
            : showUpdatedCSBPlus || showEnhancedCSBPlus
            ? renderBonusAmountPerOEForUpdatedCSBPlus()
            : renderBonusAmountPerOE()}
        </>
      )}
    </View>
  );
};
